import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { apiErrorMessage, downloadDocument, fetchDocumentBlob, useDocument } from '../api';

type Preview =
  | { kind: 'loading' }
  | { kind: 'pdf'; url: string }
  | { kind: 'text'; text: string }
  | { kind: 'table'; rows: string[][] }
  | { kind: 'html'; html: string }
  | { kind: 'error' };

/** Small RFC-4180 style CSV parser (quotes, escaped quotes, CRLF). */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charAt(i);
    if (quoted) {
      if (ch === '"' && text.charAt(i + 1) === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text.charAt(i + 1) === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}

const sanitize = (html: string) => DOMPurify.sanitize(html);

export function DocumentViewerPage() {
  const { documentId } = useParams<{ documentId: string }>();
  const doc = useDocument(documentId);
  const [preview, setPreview] = useState<Preview>({ kind: 'loading' });
  const [downloading, setDownloading] = useState(false);

  const data = doc.data;

  useEffect(() => {
    if (!data) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    setPreview({ kind: 'loading' });

    (async () => {
      try {
        const blob = await fetchDocumentBlob(data.id);
        const ext = data.fileExtension.toLowerCase().replace('.', '');
        let next: Preview;
        if (ext === 'pdf') {
          objectUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
          next = { kind: 'pdf', url: objectUrl };
        } else if (ext === 'txt') {
          next = { kind: 'text', text: await blob.text() };
        } else if (ext === 'csv') {
          next = { kind: 'table', rows: parseCsv(await blob.text()) };
        } else if (ext === 'xls' || ext === 'xlsx') {
          const XLSX = await import('xlsx');
          const wb = XLSX.read(await blob.arrayBuffer(), { type: 'array' });
          const first = wb.SheetNames[0];
          const ws = first ? wb.Sheets[first] : undefined;
          if (!ws) throw new Error('empty workbook');
          next = { kind: 'html', html: sanitize(XLSX.utils.sheet_to_html(ws)) };
        } else if (ext === 'doc' || ext === 'docx') {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const mod: any = await import('mammoth');
          const mammoth = mod.default ?? mod;
          const result = await mammoth.convertToHtml({ arrayBuffer: await blob.arrayBuffer() });
          next = { kind: 'html', html: sanitize(String(result.value)) };
        } else {
          throw new Error('unsupported');
        }
        if (cancelled) {
          if (objectUrl) URL.revokeObjectURL(objectUrl);
          return;
        }
        setPreview(next);
      } catch {
        if (!cancelled) setPreview({ kind: 'error' });
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [data]);

  const handleDownload = async () => {
    if (!data) return;
    setDownloading(true);
    try {
      await downloadDocument(data.id, data.originalFileName);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Download failed'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <ScrollPage>
      <PageHeader
        title={data?.documentName ?? 'Document'}
        description={data?.originalFileName}
        actions={
          data ? (
            <Button onClick={() => void handleDownload()} disabled={downloading}>
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Download
            </Button>
          ) : undefined
        }
      />

      {doc.isLoading && (
        <div className="flex h-64 items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading…
        </div>
      )}
      {doc.isError && <p className="text-sm text-destructive">Document not found or you do not have access.</p>}

      {data && preview.kind === 'loading' && (
        <div className="flex h-64 items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" /> Preparing preview…
        </div>
      )}
      {data && preview.kind === 'error' && (
        <p className="text-sm text-muted-foreground">
          Preview is not available for this file. Please download it to view.
        </p>
      )}
      {preview.kind === 'pdf' && (
        <iframe title="Document preview" src={preview.url} className="h-[75vh] w-full rounded-lg border border-border" />
      )}
      {preview.kind === 'text' && (
        <pre className="max-h-[75vh] overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-card p-4 text-sm">
          {preview.text}
        </pre>
      )}
      {preview.kind === 'table' && (
        <div className="max-h-[75vh] overflow-auto rounded-lg border border-border bg-card">
          <table className="w-full border-collapse text-sm">
            <tbody>
              {preview.rows.map((r, i) => (
                <tr key={i} className={i === 0 ? 'bg-muted font-medium' : undefined}>
                  {r.map((c, j) => (
                    <td key={j} className="border border-border px-2 py-1">
                      {c}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {preview.kind === 'html' && (
        <div
          className="max-h-[75vh] overflow-auto rounded-lg border border-border bg-card p-4 text-sm [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1"
          dangerouslySetInnerHTML={{ __html: preview.html }}
        />
      )}
    </ScrollPage>
  );
}
